// Gravação de áudio (recomendação do agrônomo a campo).
// No app (Capacitor) usa o plugin NATIVO @independo/capacitor-voice-recorder —
// gerencia a permissão de microfone de forma nativa (sem depender do getUserMedia
// do WebView, que falhava). No navegador usa MediaRecorder.
import { VoiceRecorder } from '@independo/capacitor-voice-recorder';
import { isNative } from '@/lib/native-auth';

export interface GravacaoAtiva {
  stop: () => Promise<{ blob: Blob; mime: string; filename: string; durMs: number } | null>;
  cancel: () => void;
}

export function suportaGravacao(): boolean {
  if (isNative()) return true;
  return typeof navigator !== 'undefined' && !!navigator.mediaDevices && typeof MediaRecorder !== 'undefined';
}

function base64ParaBlob(b64: string, mime: string): Blob {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

function extDoMime(mime: string): string {
  if (mime.includes('webm')) return 'webm';
  if (mime.includes('ogg')) return 'ogg';
  if (mime.includes('wav')) return 'wav';
  if (mime.includes('mpeg')) return 'mp3';
  return 'm4a'; // aac/mp4
}

// ── Nativo (Capacitor): plugin de voz ────────────────────────────────────────
async function iniciarNativo(): Promise<GravacaoAtiva> {
  const perm = await VoiceRecorder.requestAudioRecordingPermission();
  if (!perm.value) throw new DOMException('Permissão de microfone negada', 'NotAllowedError');
  await VoiceRecorder.startRecording();
  return {
    stop: async () => {
      const res = await VoiceRecorder.stopRecording();
      const v = res && res.value;
      if (!v || !v.recordDataBase64) return null;
      const mime = v.mimeType || 'audio/aac';
      return {
        blob: base64ParaBlob(v.recordDataBase64, mime),
        mime,
        filename: `recomendacao.${extDoMime(mime)}`,
        durMs: v.msDuration || 0,
      };
    },
    cancel: () => { void VoiceRecorder.stopRecording().catch(() => {}); },
  };
}

// ── Web (navegador): MediaRecorder ───────────────────────────────────────────
function escolherMime(): string {
  const cands = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'];
  for (const m of cands) {
    try { if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(m)) return m; } catch { /* ignore */ }
  }
  return '';
}

async function iniciarWeb(): Promise<GravacaoAtiva> {
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  const mime = escolherMime();
  const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
  const chunks: Blob[] = [];
  const inicio = Date.now();
  rec.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data); };
  rec.start();
  const cleanup = () => { try { stream.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ } };
  return {
    stop: () => new Promise((resolve) => {
      rec.onstop = () => {
        cleanup();
        const tipo = rec.mimeType || mime || 'audio/webm';
        const blob = new Blob(chunks, { type: tipo });
        resolve(blob.size ? { blob, mime: tipo, filename: `recomendacao.${extDoMime(tipo)}`, durMs: Date.now() - inicio } : null);
      };
      try { rec.stop(); } catch { cleanup(); resolve(null); }
    }),
    cancel: () => { try { rec.stop(); } catch { /* ignore */ } cleanup(); },
  };
}

export async function iniciarGravacao(): Promise<GravacaoAtiva> {
  return isNative() ? iniciarNativo() : iniciarWeb();
}
